import React, { useRef, useState } from 'react';
import { Container, Typography, Box, Card, CardContent, useTheme, IconButton, Collapse, useMediaQuery } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { motion, useInView } from 'framer-motion';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { getGlassCardStyles } from '../../shared/styles';

const contentUseCase = new ContentUseCase();

export const AboutUsPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const aboutContent = contentUseCase.getAboutUsContent(language);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});
  
  const titleRef = useRef(null);
  const cardsRef = useRef(null);
  const teamRef = useRef(null);
  
  const titleInView = useInView(titleRef, { once: true });
  const cardsInView = useInView(cardsRef, { once: true, margin: "-100px" });
  const teamInView = useInView(teamRef, { once: true, margin: "-100px" });
  
  const handleExpandClick = (index: number) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  return (
    <Box>
      <SEO page="about" />
      <Container maxWidth="xl" sx={{ py: theme.spacing(8) }}>
        <motion.div
          ref={titleRef}
          initial={{ opacity: 0, y: 20 }}
          animate={titleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: theme.spacing(6) }}>
            <Box
              component="img"
              src="/icon.png"
              alt=""
              aria-hidden="true"
              sx={{ height: { xs: 44, md: 56 }, width: 'auto' }}
            />
            <Typography variant="h2" component="h1" color="primary" sx={{ fontWeight: 800 }}>
              {aboutContent.title}
            </Typography>
          </Box>
        </motion.div>
        <motion.div
          ref={cardsRef}
          initial="hidden"
          animate={cardsInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: theme.spacing(4),
              mb: theme.spacing(4),
            }}
          >
            <motion.div variants={fadeInUp} style={{ height: '100%' }}>
              <Card sx={{ height: '100%', ...getGlassCardStyles(theme, 'default', true) }}>
                <CardContent sx={{ p: theme.spacing(4) }}>
                  <Typography variant="h4" component="h2" gutterBottom color="secondary">
                    {aboutContent.mission.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {aboutContent.mission.description}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeInUp} style={{ height: '100%' }}>
              <Card sx={{ height: '100%', ...getGlassCardStyles(theme, 'default', true) }}>
                <CardContent sx={{ p: theme.spacing(4) }}>
                  <Typography variant="h4" component="h2" gutterBottom color="secondary">
                    {aboutContent.vision.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {aboutContent.vision.description}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Box>
        </motion.div>
      </Container>

      {/* Team section - separate section */}
      <Container maxWidth="xl" sx={{ pb: theme.spacing(8) }}>
        <Box
          sx={{
            width: '100px',
            height: '2px',
            backgroundColor: 'secondary.main',
            mx: 'auto',
            mb: theme.spacing(8),
          }}
        />
        <motion.div
          ref={teamRef}
          initial="hidden"
          animate={teamInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {aboutContent.team.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: 900, mx: 'auto', mb: theme.spacing(6) }}>
            {aboutContent.team.description}
          </Typography>
        </motion.div>
        <motion.div
          initial="hidden"
          animate={teamInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                // Keep the cards centred when fewer than three members are listed.
                md: `repeat(${Math.min(aboutContent.team.members?.length || 3, 3)}, 1fr)`,
              },
              gap: theme.spacing(4),
              maxWidth: 1200,
              mx: 'auto',
              alignItems: 'stretch',
            }}
          >
            {aboutContent.team.members?.map((member: any, index: number) => {
              const isExpanded = expandedCards[index] || !isSmallScreen;
              const description = Array.isArray(member.description) ? member.description : [member.description];
              
              return (
                <motion.div
                  key={`member-${index}`}
                  variants={fadeInUp}
                  style={{ height: '100%' }}
                >
                  <Card sx={{ 
                    textAlign: 'left', 
                    p: theme.spacing(4), 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    ...getGlassCardStyles(theme, 'default', true) 
                  }}>
                    <CardContent sx={{ p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: theme.spacing(1) }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h5" component="h3" gutterBottom color="secondary" sx={{ fontWeight: 600, mb: theme.spacing(1) }}>
                            {member.name}
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ mb: theme.spacing(2), fontWeight: 500 }}>
                            {member.role}
                          </Typography>
                        </Box>
                        {isSmallScreen && (
                          <IconButton
                            onClick={() => handleExpandClick(index)}
                            size="small"
                            sx={{ ml: theme.spacing(1), mt: -1 }}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </Box>
                      {member.description && (
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit={false}>
                          <Box>
                            {description.map((paragraph: string, pIndex: number) => (
                              <Typography 
                                key={pIndex}
                                variant="body1" 
                                color="text.secondary" 
                                sx={{ 
                                  lineHeight: 1.7,
                                  mb: pIndex < description.length - 1 ? theme.spacing(2) : 0,
                                }}
                              >
                                {paragraph}
                              </Typography>
                            ))}
                          </Box>
                        </Collapse>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

